/**
 * Last-minute automatico: sconto % applicato quando la data di PARTENZA è entro
 * N giorni da oggi. Usato sia per noleggio barche (check_in) che escursioni
 * (data slot). Logica unica condivisa FE + server per evitare drift.
 */

export interface LastMinuteConfig {
  enabled?: boolean | null;
  daysBefore?: number | null;
  percent?: number | null;
}

/** Normalizza i campi DB (snake_case) → config. */
export function lastMinuteFromRow(row: {
  last_minute_enabled?: boolean | null;
  last_minute_days_before?: number | null;
  last_minute_discount_percent?: number | null;
} | null | undefined): LastMinuteConfig {
  return {
    enabled: !!row?.last_minute_enabled,
    daysBefore: row?.last_minute_days_before ?? null,
    percent: row?.last_minute_discount_percent ?? null,
  };
}

function startOfDayUtc(d: Date): number {
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
}

function toDate(v: string | Date): Date {
  if (v instanceof Date) return v;
  // 'YYYY-MM-DD' → mezzanotte UTC (coerente con startOfDayUtc)
  return new Date(`${v.slice(0, 10)}T00:00:00Z`);
}

/** Giorni interi da oggi alla partenza (>=0 futuro, <0 passato). */
export function daysUntil(startDate: string | Date, now: Date = new Date()): number {
  return Math.ceil((startOfDayUtc(toDate(startDate)) - startOfDayUtc(now)) / 86_400_000);
}

/** % di sconto applicabile (0 se non attivo / fuori finestra). */
export function lastMinutePercent(
  startDate: string | Date | null | undefined,
  cfg: LastMinuteConfig,
  now: Date = new Date(),
): number {
  if (!cfg?.enabled || !cfg.percent || !cfg.daysBefore) return 0;
  if (cfg.percent <= 0 || cfg.percent > 90) return 0; // guard
  if (!startDate) return 0;
  const d = daysUntil(startDate, now);
  if (d < 0 || d > cfg.daysBefore) return 0;
  return cfg.percent;
}

function round2(n: number): number {
  return Math.round((n + Number.EPSILON) * 100) / 100;
}

/** Sconta un importo di una % data (formula unica FE↔server per match esatto). */
export function discountByPercent(subtotal: number, percent: number): number {
  if (!percent || percent <= 0) return round2(subtotal);
  const discount = round2(subtotal * percent / 100);
  return round2(subtotal - discount);
}

/** Applica lo sconto a un subtotale (calcola la % dalla data). */
export function applyLastMinute(
  subtotal: number,
  startDate: string | Date | null | undefined,
  cfg: LastMinuteConfig,
  now: Date = new Date(),
): { discounted: number; discount: number; percent: number } {
  const percent = lastMinutePercent(startDate, cfg, now);
  if (percent <= 0) return { discounted: round2(subtotal), discount: 0, percent: 0 };
  return { discounted: discountByPercent(subtotal, percent), discount: round2(subtotal * percent / 100), percent };
}

/** Badge "offerta attiva" (config valida), indipendente dalla data selezionata. */
export function lastMinuteOfferActive(cfg: LastMinuteConfig): boolean {
  return !!cfg?.enabled && !!cfg.percent && cfg.percent > 0 && !!cfg.daysBefore && cfg.daysBefore > 0;
}
